import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import OpenInNew from "@mui/icons-material/OpenInNew";
import UploadFile from "@mui/icons-material/UploadFile";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { Dropzone } from "components/Dropzone";
import { OmnisearchAutocomplete } from "components/OmnisearchAutocomplete";
import { DocumentType, FacilityDocumentsQuery } from "generated-graphql/graphql";
import { useQueueManifestDocumentParse } from "hooks/useQueueManifestDocumentParse";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { DOCUMENTS } from "routes/Customer/Documents";
import { FACILITY_DOCUMENTS } from "routes/Customer/Facility/Documents";
import { documentTypeToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";
import { uploadFileDirectly } from "util/uploadFile";

type FileUploadStatus = {
  fileName: string;
  status: "pending" | "uploading" | "processing" | "success" | "error";
  error?: string;
  documentId?: string;
  jobId?: string;
};

type StagedDocument = {
  id: string;
  title: string;
};

type ManifestUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  facilityId?: string;
  /** Pre-fill the manifest tracking number for AI extraction override */
  manifestTrackingNumber?: string;
  /** Pre-fill the generator EPA ID for AI extraction override */
  generatorId?: string;
};

type PickableDocument = {
  id: string;
  title: string;
  fileExtension?: string | null;
  documentType: DocumentType;
  createdAt?: string | null;
};

export function ManifestUploadDialog({
  open,
  onClose,
  tenantId,
  facilityId,
  manifestTrackingNumber,
  generatorId,
}: ManifestUploadDialogProps) {
  const alerts = useAlerts();
  const { upsertDocument } = useUpsertDocument();
  const { queueManifestDocumentParse } = useQueueManifestDocumentParse();

  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mtnOverride, setMtnOverride] = useState(manifestTrackingNumber);
  const [generatorIdOverride, setGeneratorIdOverride] = useState(generatorId);

  // Track all document IDs that are staged, queued, or completed
  const disabledDocumentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const doc of stagedDocuments) ids.add(doc.id);
    for (const f of fileStatuses) {
      if (f.documentId) ids.add(f.documentId);
    }
    return ids;
  }, [stagedDocuments, fileStatuses]);

  const defaultSearch = facilityId
    ? `facilityId:${facilityId} documentType:${DocumentType.RcraManifest}`
    : `tenantId:${tenantId} documentType:${DocumentType.RcraManifest}`;

  // --- Existing document staging ---

  const handleSelectExistingDocument = useCallback(
    (doc: PickableDocument) => {
      if (disabledDocumentIds.has(doc.id)) return;
      setStagedDocuments((prev) => [...prev, { id: doc.id, title: doc.title }]);
    },
    [disabledDocumentIds]
  );

  const handleRemoveStagedDocument = useCallback((id: string) => {
    setStagedDocuments((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const handleQueueStagedDocuments = useCallback(async () => {
    if (stagedDocuments.length === 0) return;

    const documentIds = stagedDocuments.map((d) => d.id);
    const stagedNames = new Map(stagedDocuments.map((d) => [d.id, d.title]));

    // Move staged docs into file statuses as "processing"
    setFileStatuses((prev) => [
      ...prev,
      ...stagedDocuments.map((d) => ({
        fileName: d.title,
        status: "processing" as const,
        documentId: d.id,
      })),
    ]);
    setStagedDocuments([]);
    setIsProcessing(true);

    try {
      const jobs = await queueManifestDocumentParse({
        tenantId,
        documentIds,
        facilityId,
        manifestTrackingNumber: mtnOverride,
        generatorId: generatorIdOverride,
      });

      const jobIdByDocumentId = new Map(jobs.map((j) => [j.documentId, j.job.id]));

      setFileStatuses((prev) =>
        prev.map((f) => {
          if (f.status === "processing" && f.documentId && stagedNames.has(f.documentId)) {
            return {
              ...f,
              status: "success",
              jobId: jobIdByDocumentId.get(f.documentId),
            };
          }
          return f;
        })
      );

      alerts.success(
        `Manifest parsing queued for ${documentIds.length} ${pluralize("document", documentIds.length)}.`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to queue processing";
      setFileStatuses((prev) =>
        prev.map((f) => {
          if (f.status === "processing" && f.documentId && stagedNames.has(f.documentId)) {
            return { ...f, status: "error", error: message };
          }
          return f;
        })
      );
      alerts.error("Failed to queue manifest parsing.");
    } finally {
      setIsProcessing(false);
    }
  }, [
    stagedDocuments,
    tenantId,
    facilityId,
    mtnOverride,
    generatorIdOverride,
    queueManifestDocumentParse,
    alerts,
  ]);

  // --- File upload ---

  const updateFileStatusAtIndex = useCallback(
    (index: number, update: Partial<FileUploadStatus>) => {
      setFileStatuses((prev) => prev.map((f, i) => (i === index ? { ...f, ...update } : f)));
    },
    []
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!tenantId) return;

      const nonPdfFiles = acceptedFiles.filter((f) => !f.name.toLowerCase().endsWith(".pdf"));
      if (nonPdfFiles.length > 0) {
        alerts.error(
          `Only PDF files are accepted. Rejected: ${nonPdfFiles.map((f) => f.name).join(", ")}`
        );
        return;
      }

      const startIndex = fileStatuses.length;
      const newStatuses: FileUploadStatus[] = acceptedFiles.map((f) => ({
        fileName: f.name,
        status: "pending",
      }));
      setFileStatuses((prev) => [...prev, ...newStatuses]);
      setIsProcessing(true);

      const documentIds: string[] = [];

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        updateFileStatusAtIndex(startIndex + i, { status: "uploading" });
        try {
          const storageLink = await uploadFileDirectly(file, { tenantId });

          const documentId = await upsertDocument({
            documentData: {
              tenantId,
              title: file.name,
              storageLink,
              documentType: DocumentType.RcraManifest,
              fileExtension: "pdf",
            },
            currentSubmissionDocument: false,
          });

          if (documentId) {
            documentIds.push(documentId);
            updateFileStatusAtIndex(startIndex + i, {
              status: "processing",
              documentId,
            });
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : "Unknown error";
          updateFileStatusAtIndex(startIndex + i, {
            status: "error",
            error: message,
          });
        }
      }

      if (documentIds.length > 0) {
        try {
          const jobs = await queueManifestDocumentParse({
            tenantId,
            documentIds,
            facilityId,
            manifestTrackingNumber: mtnOverride,
            generatorId: generatorIdOverride,
          });

          const jobIdByDocumentId = new Map(jobs.map((j) => [j.documentId, j.job.id]));

          setFileStatuses((prev) =>
            prev.map((f) => {
              if (f.status === "processing" && f.documentId) {
                return {
                  ...f,
                  status: "success",
                  jobId: jobIdByDocumentId.get(f.documentId),
                };
              }
              return f;
            })
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to queue processing";
          setFileStatuses((prev) =>
            prev.map((f) =>
              f.status === "processing" ? { ...f, status: "error", error: message } : f
            )
          );
        }
      }

      setIsProcessing(false);

      setFileStatuses((prev) => {
        const successCount = prev.filter((f) => f.status === "success").length;
        const errorCount = prev.filter((f) => f.status === "error").length;
        if (successCount > 0) {
          alerts.success(
            `Uploaded ${successCount} ${pluralize("manifest", successCount)}. Processing queued.`
          );
        }
        if (errorCount > 0) {
          alerts.error(`${errorCount} ${pluralize("file", errorCount)} failed.`);
        }
        return prev;
      });
    },
    [
      tenantId,
      facilityId,
      mtnOverride,
      generatorIdOverride,
      fileStatuses.length,
      alerts,
      updateFileStatusAtIndex,
      upsertDocument,
      queueManifestDocumentParse,
    ]
  );

  // --- Dialog lifecycle ---

  const handleClose = () => {
    if (!isProcessing) {
      setFileStatuses([]);
      setStagedDocuments([]);
      onClose();
    }
  };

  // --- Omnisearch helpers ---

  const resultFilter = useCallback(
    (item: PickableDocument) => item.fileExtension?.toLowerCase() === "pdf",
    []
  );

  const getOptionDisabled = useCallback(
    (item: PickableDocument) => disabledDocumentIds.has(item.id),
    [disabledDocumentIds]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, item: PickableDocument) => (
      <ListItem key={item.id} {...props} disablePadding>
        <ListItemText
          primary={item.title}
          secondary={`${documentTypeToLabel(item.documentType)} ${item.createdAt ? `· ${prettyPrintDateMed(item.createdAt)}` : ""}`}
        />
      </ListItem>
    ),
    []
  );

  const omnisearchProps = {
    runInitialSearch: true,
    disabled: isProcessing,
    defaultSearch,
    placeholder: "Search RCRA manifest documents...",
    getOptionLabel: (item: PickableDocument) => item.title,
    onSelectionChange: (selected: PickableDocument | null) => {
      if (selected) {
        handleSelectExistingDocument(selected);
      }
    },
    resultFilter,
    getOptionDisabled,
    renderOption,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      title="Upload Manifest PDF"
      hideCancelButton
      primaryActionButtonProps={{
        hideStartIcon: true,
        saveText: "Close",
        onClick: handleClose,
        disabled: isProcessing,
      }}
    >
      <Stack spacing={2}>
        {facilityId && (
          <Alert severity="info" variant="outlined">
            Documents will be associated with this facility regardless of the EPA ID found in the
            manifest.
          </Alert>
        )}

        {(mtnOverride || generatorIdOverride) && (
          <Alert severity="info" variant="outlined">
            The following values will override AI extraction:
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
              {mtnOverride && (
                <Chip
                  label={`MTN: ${mtnOverride}`}
                  size="small"
                  onDelete={() => setMtnOverride(undefined)}
                  deleteIcon={<Close />}
                />
              )}
              {generatorIdOverride && (
                <Chip
                  label={`Generator ID: ${generatorIdOverride}`}
                  size="small"
                  onDelete={() => setGeneratorIdOverride(undefined)}
                  deleteIcon={<Close />}
                />
              )}
            </Stack>
          </Alert>
        )}

        {/* Existing document picker */}
        <div>
          <Typography variant="subtitle2" gutterBottom>
            Select existing documents
          </Typography>
          {facilityId ? (
            <OmnisearchAutocomplete
              {...omnisearchProps}
              dataQuery={FACILITY_DOCUMENTS}
              dataQueryVariables={{
                facilityId,
                sort: [{ field: "createdAt", sort: "desc" }],
                page: 0,
                pageSize: 20,
              }}
              getItems={(data) => data.documents.items}
            />
          ) : (
            <OmnisearchAutocomplete
              {...omnisearchProps}
              dataQuery={DOCUMENTS}
              getItems={(data) => data.documents.items}
            />
          )}

          {/* Staged documents list */}
          {stagedDocuments.length > 0 && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {stagedDocuments.map((doc) => (
                  <Chip
                    key={doc.id}
                    label={doc.title}
                    onDelete={() => handleRemoveStagedDocument(doc.id)}
                    deleteIcon={<Close />}
                    size="small"
                  />
                ))}
              </Stack>
              <Alert severity="warning" variant="outlined" icon={<WarningAmber />}>
                If any of these documents were previously parsed, the existing manifest data will be
                replaced.
              </Alert>
              <Button
                variant="contained"
                size="small"
                onClick={handleQueueStagedDocuments}
                disabled={isProcessing}
              >
                Queue {stagedDocuments.length} {pluralize("document", stagedDocuments.length)} for
                parsing
              </Button>
            </Stack>
          )}
        </div>

        <Divider />

        {/* Upload new documents */}
        <div>
          <Typography variant="subtitle2" gutterBottom>
            Upload new documents
          </Typography>
          <Dropzone
            label="Drag and drop PDF files here, or click to browse"
            dragActiveText="Drop PDF files here"
            accept=".pdf"
            multiple
            onDrop={handleDrop}
            disabled={isProcessing}
          />
        </div>

        {/* Processing status list */}
        {fileStatuses.length > 0 && (
          <List dense>
            {fileStatuses.map((file, index) => (
              <ListItem key={`${file.fileName}-${index}`}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {file.status === "pending" && <UploadFile color="disabled" fontSize="small" />}
                  {(file.status === "uploading" || file.status === "processing") && (
                    <CircularProgress size={20} />
                  )}
                  {file.status === "success" && <CheckCircle color="success" fontSize="small" />}
                  {file.status === "error" && <ErrorOutline color="error" fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={file.fileName}
                  secondary={(() => {
                    if (file.status === "error") return file.error;
                    if (file.status === "processing") return "Queuing extraction...";
                    return undefined;
                  })()}
                />
                {file.jobId && (
                  <IconButton
                    size="small"
                    href={`/staff/jobs/${file.jobId}`}
                    target="_blank"
                    title="View job"
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    </Dialog>
  );
}
